import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { BrandProvider } from './contexts/BrandContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/AppLayout';
import SignupPage from './pages/SignupPage';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import ProductSelectorPage from './pages/ProductSelectorPage';
import OnboardingSuccessPage from './pages/OnboardingSuccessPage';
import OnboardingPage from './pages/OnboardingPage';
import AdminOnboardCustomerPage from './pages/AdminOnboardCustomerPage';
import OnboardingWebsiteTemplatePage from './pages/OnboardingWebsiteTemplatePage';
import DashboardWebsitePage from './pages/DashboardWebsitePage';
import DashboardWebsiteTemplatesPage from './pages/DashboardWebsiteTemplatesPage';
import DashboardKnowledgePage from './pages/DashboardKnowledgePage';
import DashboardPage from './pages/DashboardPage';
import AssistantSettingsPage from './pages/AssistantSettingsPage';
import PhoneSettingsPage from './pages/PhoneSettingsPage';
import CallTestPage from './pages/CallTestPage';
import CallHistoryPage from './pages/CallHistoryPage';
import LeadsPage from './pages/LeadsPage';
import SavedSearchesPage from './pages/SavedSearchesPage';
import ReportsPage from './pages/ReportsPage';
import ReportSettingsPage from './pages/ReportSettingsPage';
import LandingPage from './pages/LandingPage';
import PropertiesPage from './pages/PropertiesPage';
import AgentPublicPage from './pages/AgentPublicPage';
import PublicProfilePage from './pages/PublicProfilePage';
import BillingPage from './pages/BillingPage';
import AppointmentsPage from './pages/AppointmentsPage';
import AIConfigPage from './pages/AIConfigPage';
import PhoneMenuPage from './pages/PhoneMenuPage';
import CampaignsPage from './pages/CampaignsPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import SpamManagementPage from './pages/SpamManagementPage';
import ScheduledCallsPage from './pages/ScheduledCallsPage';
import CallSimulationPage from './pages/CallSimulationPage';
import PipelinePage from './pages/PipelinePage';
import ListingsPage from './pages/ListingsPage';
import OutcomeDashboardPage from './pages/OutcomeDashboardPage';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import DemoPage from './pages/DemoPage';
import ReferralLandingPage from './pages/ReferralLandingPage';
import ReferralDashboardPage from './pages/ReferralDashboardPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyOTPPage from './pages/VerifyOTPPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import LeadInboxPage from './pages/LeadInboxPage';
import LeadDigestSettingsPage from './pages/LeadDigestSettingsPage';
import SMSConversationsPage from './pages/SMSConversationsPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogPostPage from './pages/BlogPostPage';
import GoogleAnalytics from './components/GoogleAnalytics';
import CustomerSitePage from './sites/CustomerSitePage';
import { getSiteByDomain } from './sites/registry';
import { FloatingAttribution } from './components/SiteAttribution';
import VDLayout from './voicedesk/VDLayout';
import VDOnboarding from './voicedesk/pages/VDOnboarding';
import VDDashboard from './voicedesk/pages/VDDashboard';
import VDCompanySetup from './voicedesk/pages/VDCompanySetup';
import VDDepartments from './voicedesk/pages/VDDepartments';
import VDDirectory from './voicedesk/pages/VDDirectory';
import VDRouting from './voicedesk/pages/VDRouting';
import VDAIService from './voicedesk/pages/VDAIService';
import VDBusinessHours from './voicedesk/pages/VDBusinessHours';
import VDTestIVR from './voicedesk/pages/VDTestIVR';
import VDSettings from './voicedesk/pages/VDSettings';
import VDAnalytics from './voicedesk/pages/VDAnalytics';
import TSLayout from './telesales/TSLayout';
import TSSetup from './telesales/pages/TSSetup';
import TSDashboard from './telesales/pages/TSDashboard';
import TSStaff from './telesales/pages/TSStaff';
import TSPhone from './telesales/pages/TSPhone';
import TSNumbers from './telesales/pages/TSNumbers';
import TSLeads from './telesales/pages/TSLeads';
import TSPipeline from './telesales/pages/TSPipeline';
import TSCampaigns from './telesales/pages/TSCampaigns';
import TSCallbacks from './telesales/pages/TSCallbacks';
import TSCalendar from './telesales/pages/TSCalendar';
import TSFrontDesk from './telesales/pages/TSFrontDesk';
import TSBrain from './telesales/pages/TSBrain';
import TSSupervisor from './telesales/pages/TSSupervisor';
import TSAnalytics from './telesales/pages/TSAnalytics';
import TSBilling from './telesales/pages/TSBilling';
import TSSettings from './telesales/pages/TSSettings';
import TSPhoneMenu from './telesales/pages/TSPhoneMenu';
import TSControl from './telesales/pages/TSControl';
import TSComms from './telesales/pages/TSComms';
import './App.css';

function AppRouter() {
  // Custom-domain hosting: if the current hostname matches a customer site's
  // `custom_domain`, render that site at the root (and ignore Agent9 routes).
  const customDomainSite =
    typeof window !== 'undefined' ? getSiteByDomain(window.location.hostname) : null;

  if (customDomainSite) {
    return (
      <>
        <Routes>
          <Route path="*" element={<CustomerSitePage />} />
        </Routes>
        {!customDomainSite.hide_attribution && <FloatingAttribution alwaysShow />}
      </>
    );
  }

  return (
    <>
    <FloatingAttribution />
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/blog" element={<BlogIndexPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/sites/:slug" element={<CustomerSitePage />} />
      <Route path="/sites/:slug/*" element={<CustomerSitePage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/agent/:slug" element={<AgentPublicPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/signup/verify" element={<VerifyOTPPage />} />
      <Route path="/demo/:slug" element={<DemoPage />} />
      <Route path="/r/:code" element={<ReferralLandingPage />} />

      {/* Internal admin — hidden from public nav, requires admin API key */}
      <Route path="/admin/onboard" element={<AdminOnboardCustomerPage />} />

      {/* Website Template flow */}
      <Route path="/onboarding/website-template" element={<OnboardingWebsiteTemplatePage />} />

      {/* Legacy VoiceDesk auth routes — redirect to unified */}
      <Route path="/voicedesk/login" element={<Navigate to="/login" replace />} />
      <Route path="/voicedesk/signup" element={<Navigate to="/signup" replace />} />
      <Route path="/voicedesk/forgot-password" element={<Navigate to="/login" replace />} />
      <Route path="/voicedesk/reset-password" element={<Navigate to="/reset-password" replace />} />

      {/* Product Selector — for multi-product users */}
      <Route path="/select-product" element={
        <ProtectedRoute><ProductSelectorPage /></ProtectedRoute>
      } />

      {/* Onboarding */}
      <Route path="/onboarding/success" element={
        <ProtectedRoute><OnboardingSuccessPage /></ProtectedRoute>
      } />
      <Route path="/onboarding" element={
        <ProtectedRoute><OnboardingPage /></ProtectedRoute>
      } />

      {/* Agent9 — Protected routes with AppLayout */}
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/dashboard/website" element={<DashboardWebsitePage />} />
        <Route path="/dashboard/website/templates" element={<DashboardWebsiteTemplatesPage />} />
        <Route path="/dashboard/knowledge" element={<DashboardKnowledgePage />} />
        <Route path="/settings/assistant" element={<AssistantSettingsPage />} />
        <Route path="/settings/phone" element={<PhoneSettingsPage />} />
        <Route path="/settings/phone-menu" element={<PhoneMenuPage />} />
        <Route path="/call-test" element={<CallTestPage />} />
        <Route path="/call-history" element={<CallHistoryPage />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/leads/inbox" element={<LeadInboxPage />} />
        <Route path="/sms/conversations" element={<SMSConversationsPage />} />
        <Route path="/settings/lead-digest" element={<LeadDigestSettingsPage />} />
        <Route path="/analytics" element={<OutcomeDashboardPage />} />
        <Route path="/pipeline" element={<PipelinePage />} />
        <Route path="/listings" element={<ListingsPage />} />
        <Route path="/saved-searches" element={<SavedSearchesPage />} />
        <Route path="/properties" element={<PropertiesPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/settings/reports" element={<ReportSettingsPage />} />
        <Route path="/report-settings" element={<ReportSettingsPage />} />
        <Route path="/settings/public-profile" element={<PublicProfilePage />} />
        <Route path="/settings/billing" element={<BillingPage />} />
        <Route path="/appointments" element={<AppointmentsPage />} />
        <Route path="/ai-config" element={<AIConfigPage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/scheduled-calls" element={<ScheduledCallsPage />} />
        <Route path="/settings/account" element={<AccountSettingsPage />} />
        <Route path="/spam" element={<SpamManagementPage />} />
        <Route path="/call-simulation" element={<CallSimulationPage />} />
        <Route path="/referrals" element={<ReferralDashboardPage />} />
      </Route>

      {/* VoiceDesk — layout-wrapped pages */}
      <Route path="/voicedesk/onboarding" element={<VDOnboarding />} />
      <Route path="/voicedesk" element={<VDLayout />}>
        <Route path="dashboard" element={<VDDashboard />} />
        <Route path="company" element={<VDCompanySetup />} />
        <Route path="departments" element={<VDDepartments />} />
        <Route path="directory" element={<VDDirectory />} />
        <Route path="routing" element={<VDRouting />} />
        <Route path="ai" element={<VDAIService />} />
        <Route path="hours" element={<VDBusinessHours />} />
        <Route path="test" element={<VDTestIVR />} />
        <Route path="analytics" element={<VDAnalytics />} />
        <Route path="settings" element={<VDSettings />} />
        <Route index element={<Navigate to="/voicedesk/dashboard" replace />} />
      </Route>

      {/* Tele Sale — company phone system for sales teams */}
      <Route path="/telesales" element={<TSLayout />}>
        <Route path="setup" element={<TSSetup />} />
        <Route path="dashboard" element={<TSDashboard />} />
        <Route path="numbers" element={<TSNumbers />} />
        <Route path="control" element={<TSControl />} />
        <Route path="staff" element={<TSStaff />} />
        <Route path="communications" element={<TSComms />} />
        <Route path="phone" element={<TSPhone />} />
        <Route path="leads" element={<TSLeads />} />
        <Route path="pipeline" element={<TSPipeline />} />
        <Route path="campaigns" element={<TSCampaigns />} />
        <Route path="callbacks" element={<TSCallbacks />} />
        <Route path="calendar" element={<TSCalendar />} />
        <Route path="front-desk" element={<TSFrontDesk />} />
        <Route path="phone-menu" element={<TSPhoneMenu />} />
        <Route path="ai" element={<TSBrain />} />
        <Route path="supervisor" element={<TSSupervisor />} />
        <Route path="analytics" element={<TSAnalytics />} />
        <Route path="billing" element={<TSBilling />} />
        <Route path="settings" element={<TSSettings />} />
        <Route index element={<Navigate to="/telesales/dashboard" replace />} />
      </Route>

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider defaultTheme="light">
        <BrandProvider>
          <AuthProvider>
            <BrowserRouter>
              <GoogleAnalytics />
              <AppRouter />
            </BrowserRouter>
            <Toaster position="top-right" richColors />
            <PWAInstallPrompt />
          </AuthProvider>
        </BrandProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
